
import React, { useEffect, useRef, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import Layout from "../Components/Layout";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { BigText, LightOne } from "../Assets/CSS/Text";
import ConfirmationModal from "../Components/Model/ConfirmationModal";

const ApplyLeaveForm = () => {
  const [leaveType, setLeaveType] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [reason, setReason] = useState("");
  const [showPrompt, setShowPrompt] = useState(false);
  const [leaveApplyLoading, setLeaveApplyLoading] = useState(false);

  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  const navigate = useNavigate();

  const openDatePicker = (ref) => {
    if (ref.current) {
      try {
        ref.current.showPicker();
      } catch (error) {
        ref.current.focus();
      }
    }
  };

  const cancelLeave = () => {
    setShowPrompt(false);
  };

  const handleApplyConfirm = async () => {
    const token = localStorage.getItem("token");

    const today = new Date().toLocaleDateString("en-CA");

    // Validation
    if (!leaveType || !fromDate || !toDate || !reason.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (fromDate < today || toDate < today) {
      toast.error("Leave dates cannot be in the past.");
      return;
    }

    if (new Date(toDate) < new Date(fromDate)) {
      toast.error("End date cannot be earlier than start date.");
      return;
    }

    const payload = {
      leaveType,
      startDate: fromDate,
      endDate: toDate,
      reason,
    };

    setLeaveApplyLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/v1/leaves`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Leave applied successfully:", response.data);

      setShowPrompt(false);

      toast.success("Leave applied successfully!", {
        position: "top-right",
        autoClose: 2000,
      });

      setTimeout(() => {
        navigate("/leave-management");
      }, 2000);

    } catch (error) {
      console.error("Error applying for leave:", error);

      toast.error(
        error.response?.data?.message ||
        "Failed to apply for leave."
      );

    } finally {
      setLeaveApplyLoading(false);
    }
  };

  return (
    <Layout>
      <ToastContainer
        position="top-right"
        autoClose={2000}
      />

      <div className="container-fluid py-3">
        <div
          className="bg-white rounded-3 p-3"
          style={{
            maxWidth: "390px",
            margin: "2px",
          }}
        >
          {/* Back Button */}
          <Icon
            icon="material-symbols:arrow-back-rounded"
            className="fs-5 mb-3"
            style={{
              color: "#606060",
              cursor: "pointer",
            }}
            onClick={() => window.history.back()}
          />

          {/* Heading */}
          <h6 style={BigText}>
            Apply for Leave:
          </h6>

          <p style={LightOne}>
            For leave application fill out the below details
          </p>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              setShowPrompt(true);
            }}
          >
            {/* Leave Type */}
            <div className="mb-2">
              <label
                className="form-label mb-1"
                style={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#505050",
                }}
              >
                Leave Type*
              </label>

              <select
                className="form-select"
                value={leaveType}
                onChange={(e) =>
                  setLeaveType(e.target.value)
                }
                required
                style={{ fontSize: "14px" }}
              >
                <option value="">
                  Select Leave Type
                </option>
                <option value="Sick_Leave">
                  Sick Leave
                </option>
                <option value="Casual_Leave">
                  Casual Leave
                </option>
                <option value="Earned_Leave">
                  Earned Leave
                </option>
              </select>
            </div>

            {/* Date Duration */}
            <div className="mb-2">
              <label
                className="form-label mb-2"
                style={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#505050",
                }}
              >
                Select Duration*
              </label>

              <div className="row g-2">
                {/* From Date */}
                <div className="col-6">
                  <label
                    className="form-label mb-1"
                    style={{
                      fontSize: "14px",
                      color: "#505050",
                    }}
                  >
                    From*
                  </label>

                  <input
                    ref={fromDateRef}
                    type="date"
                    className="form-control"
                    value={fromDate}
                    onChange={(e) => {
                      setFromDate(e.target.value);

                      if (
                        toDate &&
                        e.target.value > toDate
                      ) {
                        setToDate("");
                      }
                    }}
                    onClick={() =>
                      openDatePicker(fromDateRef)
                    }
                    required
                    style={{ fontSize: "13px" }}
                  />
                </div>

                {/* To Date */}
                <div className="col-6">
                  <label
                    className="form-label mb-1"
                    style={{
                      fontSize: "14px",
                      color: "#505050",
                    }}
                  >
                    To*
                  </label>

                  <input
                    ref={toDateRef}
                    type="date"
                    className="form-control"
                    value={toDate}
                    onChange={(e) =>
                      setToDate(e.target.value)
                    }
                    onClick={() =>
                      openDatePicker(toDateRef)
                    }
                    min={fromDate || undefined}
                    required
                    style={{ fontSize: "13px" }}
                  />
                </div>
              </div>
            </div>

            {/* Reason */}
            <div className="mb-3">
              <label
                className="form-label mb-1"
                style={{
                  fontSize: "14px",
                  fontWeight: 400,
                  color: "#505050",
                }}
              >
                Reason*
              </label>

              <textarea
                className="form-control"
                rows="3"
                value={reason}
                onChange={(e) =>
                  setReason(e.target.value)
                }
                required
                style={{
                  fontSize: "14px",
                  resize: "vertical",
                  minHeight: "80px",
                }}
              />
            </div>

            {/* Submit Button */}
            <div className="d-flex justify-content-center align-items-center mt-4 mb-4">
              <button
                type="submit"
                className="btn w-100 d-flex justify-content-center align-items-center gap-2"
                style={{
                  border: "1.5px solid #00AFEF",
                  color: "#00AFEF",
                  backgroundColor: "#FFFFFF",
                  fontSize: "14px",
                  fontWeight: 500,
                  borderRadius: "5px",
                  padding: "9px",
                }}
              >
                <Icon icon="grommet-icons:send" />
                Send Application
              </button>
            </div>
          </form>

          {/* Confirmation Modal */}
          <ConfirmationModal
            show={showPrompt}
            title="Confirm Leave Apply"
            message="Are you sure you want to Apply for Leave?"
            icon="et:hourglass"
            confirmLabel="Apply"
            cancelLabel="Cancel"
            onConfirm={handleApplyConfirm}
            onCancel={cancelLeave}
            loading={leaveApplyLoading}
            loadingMessage="Processing ..."
          />
        </div>
      </div>
    </Layout>
  );
};

export default ApplyLeaveForm;
